import {io, Socket} from "socket.io-client";
import {getKeycloakInstance} from "./keycloak";
import {removeInstance, updateInstance, fetchInstances} from "../store/slices/instancesSlice";
import {removeNotification, setNotifications, updateNotifications} from "../store/slices/notificationSlice";
import {store} from '../store/store';
import {normalizeInstance} from "./normalizeInstance";
import {v4 as uuidv4} from "uuid";
import {removeVolume, updateVolume} from "../store/slices/volumesSlice";
import {removeSnapshot, updateSnapshot} from "../store/slices/snapshotsSlice";
import {removeBackup, updateBackup} from "../store/slices/backupsSlice";
import {removeCluster, updateCluster, fetchClusters} from "../store/slices/clustersSlice";
import {removeLoadBalancer, updateLoadBalancer} from "../store/slices/loadBalancersSlice";
import {fetchSubscriptionInfo} from "../store/slices/paymentSlice";
import {fetchBillingForecast} from "../store/slices/billingSlice";
import {fetchBillingHistory, resetHistory} from "../store/slices/billingHistorySlice";

let socket: Socket | null = null;

export function connectSocket() {
    const keycloak = getKeycloakInstance();

    if (!keycloak || !keycloak.tokenParsed) {
        throw new Error("User not authenticated, token missing");
    }

    const os_id = (keycloak.tokenParsed as any).os_id;

    if (!os_id) {
        console.debug("OpenStack not ready yet (no os_id). Skipping socket connection for now.");
        return null;
    }

    if (!socket) {
        socket = io(process.env.REACT_APP_BASE_URL || "http://localhost:8000", {
            query: {os_id},
            auth: callback => callback({token:getKeycloakInstance()?.token}),
            transports: ["websocket"],
        });

        const DISCONNECT_NOTIFICATION_ID = "socket-disconnected";

        socket.on("connect", () => {
            console.debug("Socket connected");
            // Clear any lingering disconnect banner
            const current = store.getState().notifications.data;
            const banner = current.find((n: any) => n.id === DISCONNECT_NOTIFICATION_ID);
            if (banner) store.dispatch(removeNotification(banner));
        });

        socket.on("disconnect", () => {
            console.debug("Socket disconnected");
            store.dispatch(updateNotifications({
                id: DISCONNECT_NOTIFICATION_ID,
                message: "Real-time updates offline. Reconnecting…",
                type: "error",
                autoDismiss: false,
            }));
        });

        socket.on("instance:update", (data) => {
            const normalizedInstance = normalizeInstance(data.instance);
            store.dispatch(updateInstance(normalizedInstance));
        });

        socket.on('update', (data) => {
            if (data?.registration_account || data?.type === 'registration_account' || data?.plan_policy_changed) {
                window.dispatchEvent(new Event('registration-account-changed'));
                store.dispatch(fetchBillingForecast());
                if(data?.plan_policy_changed)store.dispatch(fetchInstances());
            }
        });

        socket.on("instance:remove", (data) => {
            store.dispatch(removeInstance({id: data.id}));
        });

        socket.on("volume:update", (data) => {
            store.dispatch(updateVolume(data.volume));
        });

        socket.on("volume:remove", (data) => {
            store.dispatch(removeVolume({id: data.id}));
        });

        socket.on("snapshot:update", (data) => {
            store.dispatch(updateSnapshot(data.snapshot));
        });

        socket.on("snapshot:remove", (data) => {
            store.dispatch(removeSnapshot({id: data.id}));
        });

        socket.on("backup:update", (data) => {
            store.dispatch(updateBackup(data.backup));
        });

        socket.on("backup:remove", (data) => {
            store.dispatch(removeBackup({id: data.id}));
        });
        
        socket.on("cluster:update", (data) => {
            store.dispatch(updateCluster(data.cluster));
        });

        socket.on("cluster:remove", (data) => {
            store.dispatch(removeCluster({id: data.id}));
        });

        socket.on("loadbalancer:update", (data) => {
            store.dispatch(updateLoadBalancer(data.loadbalancer));
        });

        socket.on("loadbalancer:remove", (data) => {
            store.dispatch(removeLoadBalancer({id: data.id}));
        });

        socket.on("notification", (data) => {

            const id = uuidv4();
            const notification = {id, message: data.message, type: data.type, autoDismiss: true,};

            store.dispatch(updateNotifications(notification));

            setTimeout(() => {
                store.dispatch(removeNotification(notification));
            }, 5000);
        });

        socket.on("longnotification", (data) => {
            // Ensure only the latest long-running status is visible
            const current = store.getState().notifications.data;
            // If this is a completion message, clear all persistent notifications; otherwise clear non-error persistent ones
            const toRemove = current.filter((n: any) => n.autoDismiss === false && ((data.type === 'success' || data.type === 'info') ? true : n.type !== 'error'));
            toRemove.forEach((n: any) => store.dispatch(removeNotification(n)));

            const id = uuidv4();
            const isSuccess = data.type === 'success' || data.message === 'COMPLETED';
            const isError = data.type === 'error';
            const notification = {id, message: data.message, type: data.type, autoDismiss: (isSuccess || isError) ? true : false};

            store.dispatch(updateNotifications(notification));

            if (data.message === 'UPDATING_CLUSTER_WITH_NEW_IMAGE') {
                store.dispatch(fetchClusters());
                if (window.location.pathname !== '/category/compute/clusters') {
                    window.location.assign('/category/compute/clusters');
                }
            }

            if (isSuccess || isError) {
                if (data.message === 'SNAPSHOT_COMPLETED') {
                    store.dispatch(fetchInstances());
                }
                if (data.message === 'SUBSCRIPTION_CANCELLED') {
                    store.dispatch(fetchSubscriptionInfo());
                    store.dispatch(resetHistory());
                    store.dispatch(fetchBillingHistory(1));
                    store.dispatch(fetchBillingForecast());
                }
                setTimeout(() => {
                    store.dispatch(removeNotification(notification));
                }, isError ? 10000 : 5000);
            }
        })

        socket.on("error", (data) => {
            // Clear any in-progress long notifications when a failure happens
            const current = store.getState().notifications.data;
            current.filter((n: any) => n.autoDismiss === false && n.type !== 'error')
                .forEach((n: any) => store.dispatch(removeNotification(n)));

            const id = uuidv4();
            const notification = {id, message: data.message, type: data.type, autoDismiss: true,};

            store.dispatch(updateNotifications(notification));

            setTimeout(() => {
                store.dispatch(removeNotification(notification));
            }, 10000); // Errors shown for 10 seconds
        });
    }

    return socket;
}
